// Copilot's page actions, shared by the side panel and the full Copilot page:
// the Open buttons under an answer, going to the page Copilot chose (with a
// form draft handed over first) and pointing at the control it named.
import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ActionRow, OpenButton, ChoiceButton, ChoiceQuestion } from "./CopilotPanelStyle";

/** Find a visible control by its exact label (outside the panel), scroll to it and pulse it. */
export function highlightControl(label) {
  const want = String(label || "").trim().toLowerCase();
  if (!want) return;
  const started = Date.now();
  const norm = (n) => (n.textContent || "").replace(/\s+/g, " ").trim().toLowerCase();
  const attr = (n, a) => String((n.getAttribute && n.getAttribute(a)) || "").trim().toLowerCase();
  const visible = (n) => { const r = n.getBoundingClientRect(); return r.width > 0 && r.height > 0; };
  const find = () => {
    const panel = document.querySelector('[aria-label="Copilot"][role="complementary"]');
    const main = document.querySelector(".mainContent") || document.body;
    const outside = (n) => !(panel && panel.contains(n));
    // A control named for Copilot (data-copilot-control), then one labelled
    // for screen readers or with a tooltip title (icon-only buttons).
    for (const a of ["data-copilot-control", "aria-label", "title"]) {
      const named = Array.from(main.querySelectorAll(`[${a}]`)).find((n) => outside(n) && attr(n, a) === want && visible(n));
      if (named) return named;
    }
    // The deepest visible element whose text is exactly the label (controls
    // are not always <button>s), then its clickable container if it has one.
    const hits = Array.from(main.querySelectorAll("*")).filter((n) => {
      if (!outside(n)) return false;
      if (norm(n) !== want) return false;
      if (Array.from(n.children).some((c) => norm(c) === want)) return false;
      return visible(n);
    });
    if (!hits.length) return null;
    const el = hits[0];
    return el.closest('button, a, [role="button"], [role="tab"]') || el;
  };
  const tick = () => {
    const el = find();
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("copilot-highlight");
      // Stays until the person clicks anywhere, or 10 seconds.
      const clear = () => { el.classList.remove("copilot-highlight"); document.removeEventListener("mousedown", clear, true); };
      setTimeout(() => document.addEventListener("mousedown", clear, true), 300);
      setTimeout(clear, 10000);
    } else if (Date.now() - started < 6000) setTimeout(tick, 300);
  };
  setTimeout(tick, 150);
}

/** What an action's button says. */
export const actionLabel = (a) => (a.prefill ? "Open the filled form" : a.highlight ? `Show me: ${a.highlight}` : `Open ${a.label}`);

/**
 * go(target): open a page Copilot chose. Hands over a form draft first, then
 * points at the control it named once the page has rendered it. Only paths
 * inside the app; a URL Copilot did not get from the page catalogue is never
 * followed. onLeave(url) runs before leaving for another page.
 */
export function useCopilotGo({ onLeave } = {}) {
  const navigate = useNavigate();
  return useCallback((target) => {
    const t = typeof target === "string" ? { url: target } : target || {};
    const url = t.url;
    if (!url || !url.startsWith("/") || url.startsWith("//")) return;
    if (t.prefill && t.prefill.form_data) {
      try { sessionStorage.setItem("copilot_prefill", JSON.stringify(t.prefill)); } catch (_) { /* private mode */ }
    }
    if (url !== window.location.pathname || t.prefill) {
      if (onLeave) onLeave(url);
      navigate(url);
    }
    if (t.highlight) highlightControl(t.highlight);
  }, [navigate, onLeave]);
}

const isChoice = (a) => a && a.type === "choose";

/** The Open buttons under an answer (a clarifying question has its own). */
export function NavigateActions({ actions, onGo }) {
  const opens = (actions || []).filter((a) => a && !isChoice(a));
  if (!opens.length) return null;
  return (
    <ActionRow>
      {opens.map((a, i) => <OpenButton key={i} type="button" onClick={() => onGo(a)}>{actionLabel(a)}</OpenButton>)}
    </ActionRow>
  );
}

const same = (a, b) => String(a || "").replace(/\s+/g, " ").trim().toLowerCase() === String(b || "").replace(/\s+/g, " ").trim().toLowerCase();

/** What the person answered a clarifying question with: the next message after it, if any. */
export function answerAfter(messages, message) {
  const i = (messages || []).indexOf(message);
  if (i === -1) return null;
  for (let j = i + 1; j < messages.length; j += 1) if (messages[j].role === "user") return messages[j];
  return null;
}

/**
 * A clarifying question Copilot asked, as answers to tap plus "Something
 * else" for a typed answer. Tapping sends the option as the person's message
 * (with what it points at); once answered, the options stay as a record.
 *   onPick(option)  sends the answer
 *   onOther()       puts the person in the message box
 *   answer          the message that answered it (null while open)
 *   disabled        while Copilot is still working
 *   content         the answer text above, so the question is not repeated
 */
export function ChoiceActions({ actions, onPick, onOther, answer = null, disabled = false, content = "" }) {
  const choices = (actions || []).filter(isChoice);
  if (!choices.length) return null;
  return choices.map((c, k) => {
    const options = (c.options || []).filter((o) => o && o.label);
    const picked = answer ? options.find((o) => same(o.label, answer.content)) : null;
    const spent = !!answer || disabled;
    const showQuestion = c.question && !String(content || "").includes(String(c.question).trim());
    return (
      <div key={k} role="group" aria-label={c.question || "Choose an answer"}>
        {showQuestion ? <ChoiceQuestion>{c.question}</ChoiceQuestion> : null}
        <ActionRow>
          {options.map((o, i) => (
            <ChoiceButton key={i} type="button" disabled={spent} $picked={picked === o} aria-pressed={picked === o}
              onClick={() => onPick(o)}>{o.label}</ChoiceButton>
          ))}
          {c.free_text !== false ? (
            <ChoiceButton type="button" disabled={spent} $picked={!!answer && !picked} aria-pressed={!!answer && !picked}
              onClick={() => onOther()}>Something else</ChoiceButton>
          ) : null}
        </ActionRow>
      </div>
    );
  });
}
